<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  ManufacturerType,
  ManufacturerPayloadType,
} from "@/common/types/manufacturer";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: ManufacturerType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  owner_id: props.data?.manufacturer_id,
  name: "",
  web_site: "",
  status: "ACTIVE",
  description: "",
  foundation_date: "",
  country_of_origin: "",
  city_of_origin: "",
  closing_date: "",
  fusion_date: "",
  sale_date: "",
};

const payload = ref<ManufacturerPayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const {
    name,
    web_site,
    status,
    description,
    foundation_date,
    country_of_origin,
    city_of_origin,
    closing_date,
    fusion_date,
    sale_date,
  } = props.data;

  payload.value = {
    ...payload.value,
    name,
    web_site,
    status,
    description,
    foundation_date,
    country_of_origin,
    city_of_origin,
    closing_date,
    fusion_date,
    sale_date,
  };
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-manufacturer">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('common.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("common.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.web_site"
      :value="payload.web_site"
      placeholder="exe: https://www.website.com"
      @update:modelValue="debouceUpdate('web_site')"
    >
      <template #label> {{ t("common.labels.web_site") }}</template>
    </base-input>

    <div class="flex gap-4">
      <base-input
        v-model="payload.country_of_origin"
        :value="payload.country_of_origin"
        :placeholder="t('common.placeholders.country_of_origin')"
        @update:modelValue="debouceUpdate('country_of_origin')"
      >
        <template #label>
          {{ t("common.labels.country_of_origin") }}
        </template>
      </base-input>

      <base-input
        v-model="payload.city_of_origin"
        :value="payload.city_of_origin"
        :placeholder="t('common.placeholders.city_of_origin')"
        @update:modelValue="debouceUpdate('city_of_origin')"
      >
        <template #label> {{ t("common.labels.city_of_origin") }}</template>
      </base-input>
    </div>

    <div class="flex gap-4">
      <base-input
        v-model="payload.foundation_date"
        :value="payload.foundation_date"
        :placeholder="t('common.placeholders.date_example')"
        @update:modelValue="debouceUpdate('foundation_date')"
      >
        <template #label> {{ t("common.labels.foundation_date") }}</template>
      </base-input>

      <base-input
        v-model="payload.closing_date"
        :value="payload.closing_date"
        :placeholder="t('common.placeholders.date_example')"
        @update:modelValue="debouceUpdate('closing_date')"
      >
        <template #label> {{ t("common.labels.closing_date") }}</template>
      </base-input>
    </div>

    <div class="flex gap-4">
      <base-input
        v-model="payload.fusion_date"
        :value="payload.fusion_date"
        :placeholder="t('common.placeholders.date_example')"
        @update:modelValue="debouceUpdate('fusion_date')"
      >
        <template #label> {{ t("common.labels.fusion_date") }}</template>
      </base-input>

      <base-input
        v-model="payload.sale_date"
        :value="payload.sale_date"
        :placeholder="t('common.placeholders.date_example')"
        @update:modelValue="debouceUpdate('sale_date')"
      >
        <template #label> {{ t("common.labels.sale_date") }}</template>
      </base-input>
    </div>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionsStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }}</template>
    </base-select>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("common.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-manufacturer {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
